// src/utils/helpers.js
// ─────────────────────────────────────────────────────────────────────────────
//  Canonical shared utility barrel.
//  Exports: cn, date formatters, ministry epoch helpers, app-wide constants.
//
//  shadcn/ui components import `cn` via @/utils/utils (shim) — do not remove
//  utils/utils.js until the vite.config.js alias is updated to point here.
// ─────────────────────────────────────────────────────────────────────────────
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, differenceInWeeks, differenceInMonths } from 'date-fns';

// ── Tailwind class merger ─────────────────────────────────────────────────────
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// ── Date formatters ───────────────────────────────────────────────────────────
export const formatDate        = (date) => format(date, 'yyyy-MM-dd');
export const formatDisplayDate = (date) => format(date, 'MMMM dd, yyyy');
export const formatDayOfWeek   = (date) => format(date, 'EEEE');

// ── Ministry epoch ────────────────────────────────────────────────────────────
// The default epoch (March 1, 2026) is used when the user hasn't set a
// custom ministry start date. To override, store an ISO date string in
// budgetSettings.ministryStartDate via ExpenseLedger / Settings.
//
// Usage with a user-configured epoch:
//   const [budgetSettings] = useSyncStore('budgetSettings', {});
//   const weekNum  = getWeekNumber(new Date(),  budgetSettings?.ministryStartDate);
//   const monthNum = getMonthNumber(new Date(), budgetSettings?.ministryStartDate);
export const DEFAULT_MINISTRY_START = '2026-03-01';

const resolveEpoch = (isoDateOverride) => {
  if (isoDateOverride) {
    const d = new Date(isoDateOverride);
    if (!isNaN(d)) return d;
  }
  return new Date(2026, 2, 1); // March 1, 2026
};

export const getWeekNumber = (date, ministryStartIso) => {
  return differenceInWeeks(date, resolveEpoch(ministryStartIso)) + 1;
};

export const getMonthNumber = (date, ministryStartIso) => {
  return differenceInMonths(date, resolveEpoch(ministryStartIso)) + 1;
};

// ── Generations ───────────────────────────────────────────────────────────────
// Values use exact strings stored in contacts — must match PeopleKeeper's
// getGenerationFromAge logic. 'Millennial' has no trailing 's'.
export const GENERATIONS = [
  { value: 'Alpha',      label: 'Gen Alpha (born 2013+, under 13)',     years: '2013+' },
  { value: 'Gen Z',      label: 'Gen Z (1997–2012, ages 13–27)',        years: '1997-2012' },
  { value: 'Millennial', label: 'Millennial (1981–1996, ages 28–43)',   years: '1981-1996' },
  { value: 'Gen X',      label: 'Gen X (1965–1980, ages 44–59)',        years: '1965-1980' },
  { value: 'Boomer',     label: 'Boomer (1946–1964, ages 60–77)',       years: '1946-1964' },
  { value: 'Silent',     label: 'Silent Generation (born before 1946)', years: 'pre-1946' },
];

// ── Expense categories ────────────────────────────────────────────────────────
export const EXPENSE_CATEGORIES = [
  'Transport',
  'Food/Hospitality',
  'Materials',
  'Digital Ministry',
  'Venue',
  'Prayer/Fasting',
  'Contingency',
  'Savings',
  'Tithes & Offering',
  'Outgoing Support',
  'Other',
];

// ── Currency ──────────────────────────────────────────────────────────────────
export const USD_TO_PHP = 58;

// ── Deprecated budget constants ───────────────────────────────────────────────
// 🟡 WARNING: MONTHLY_BUDGET_PHP and MONTHLY_BUDGET_USD are deprecated.
// Budget is now stored per-user in useSyncStore('budgetSettings').
// Replace all import sites with: budgetSettings?.monthlyPhp ?? 0
// These shims exist only to prevent build failures during migration.
// TODO: Remove once all call sites have been updated.
export const MONTHLY_BUDGET_PHP = 0;
export const MONTHLY_BUDGET_USD = 0;